import { useState, useCallback } from 'react';
import { Card, Table, Row, Col, Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaFutbol, FaInfoCircle, FaExchangeAlt, FaArrowRight, FaCheck, FaClock, FaUserPlus, FaCalendarAlt, FaGift } from 'react-icons/fa';
import { toast } from 'react-toastify';
import jornadaService from '../../services/jornadaService';
import { formatFechaJornadaCorta } from '../../utils/fechaUtils';

const POSICIONES = [
  { value: 'GK', label: 'Por' },
  { value: 'DF', label: 'Cen/Lat' },
  { value: 'MD', label: 'Med/Ext' },
  { value: 'FW', label: 'Del/MP' },
  { value: 'SUP', label: 'Cualquiera' }
];
const POS_LABEL = { 'GK': 'Por', 'DF': 'Cen/Lat', 'MD': 'Med/Ext', 'FW': 'Del/MP', 'SUP': 'Cualquiera' };
const MODALIDADES = ['F5', 'F8', 'F11'];

const ProposedMatchesView = ({ propuestas, setPropuestas, loading, jornadaId, torneoId, onConfirmed }) => {
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveData, setMoveData] = useState(null);
  const [showSolapadosModal, setShowSolapadosModal] = useState(false);
  const [solapadosData, setSolapadosData] = useState(null); // { borradorId, solapados }

  // Estado para modal de añadir jugador
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [addPlayerTarget, setAddPlayerTarget] = useState(null); // { borradorId, equipo }
  const [addPlayerSearch, setAddPlayerSearch] = useState('');
  const [addPlayerResults, setAddPlayerResults] = useState([]);
  const [addPlayerLoading, setAddPlayerLoading] = useState(false);
  const [addingPlayer, setAddingPlayer] = useState(false);

  // Mapa de traducciones de posiciones
  const posIdMap = { 1: 'Por', 2: 'Cen/Lat', 3: 'Med/Ext', 4: 'Del/MP', 5: 'Cualquiera' };
  const getPosNombre = (id) => posIdMap[id] || 'N/A';

  const POS_ROW_COLOR = {
    'GK': '#fff8e1',  // amarillo suave - portero
    'DF': '#e8f5e9',  // verde suave - defensa
    'MD': '#e3f2fd',  // azul suave - centrocampista
    'FW': '#fce4ec',  // rojo suave - delantero
    'SUP': '#f3e5f5'  // morado suave - suplente
  };

  // === HANDLERS DE EDICIÓN ===

  // Guardar cambios de un borrador en backend
  const saveBorrador = useCallback(async (borradorId, datosJson, extraFields = {}) => {
    setSaving(true);
    try {
      await jornadaService.updateBorrador(borradorId, { datos_json: datosJson, ...extraFields });
    } catch (err) {
      toast.error('Error al guardar borrador');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }, []);

  // Punto 1: Mover jugador al otro equipo (mismo partido)
  const handleSwapEquipo = useCallback((propIdx, futbolistaId) => {
    const updated = [...propuestas];
    const datos = [...(Array.isArray(updated[propIdx].datos_json) ? updated[propIdx].datos_json : [])];
    const jIdx = datos.findIndex(j => j.futbolista_id === futbolistaId);
    if (jIdx === -1) return;
    datos[jIdx] = { ...datos[jIdx], equipo: datos[jIdx].equipo === 1 ? 2 : 1 };
    updated[propIdx] = { ...updated[propIdx], datos_json: datos };
    setPropuestas(updated);
    saveBorrador(updated[propIdx].id, datos);
  }, [propuestas, setPropuestas, saveBorrador]);

  // Punto 3: Cambiar posición de un jugador
  const handleChangePosicion = useCallback((propIdx, futbolistaId, nuevaPosicion) => {
    const updated = [...propuestas];
    const datos = [...(Array.isArray(updated[propIdx].datos_json) ? updated[propIdx].datos_json : [])];
    const jIdx = datos.findIndex(j => j.futbolista_id === futbolistaId);
    if (jIdx === -1) return;
    datos[jIdx] = { ...datos[jIdx], posicion_asignada: nuevaPosicion, tipo_asignacion: 'Manual' };
    updated[propIdx] = { ...updated[propIdx], datos_json: datos };
    setPropuestas(updated);
    saveBorrador(updated[propIdx].id, datos);
  }, [propuestas, setPropuestas, saveBorrador]);

  // Punto 4: Cambiar modalidad de un partido
  const handleChangeModalidad = useCallback((propIdx, nuevaModalidad) => {
    const updated = [...propuestas];
    updated[propIdx] = { ...updated[propIdx], modalidad: nuevaModalidad };
    setPropuestas(updated);
    saveBorrador(updated[propIdx].id, updated[propIdx].datos_json, { modalidad: nuevaModalidad });
  }, [propuestas, setPropuestas, saveBorrador]);

  // Punto 5: Cambiar hora de un partido
  const handleChangeHora = useCallback((propIdx, nuevaHora) => {
    const updated = [...propuestas];
    updated[propIdx] = { ...updated[propIdx], hora: nuevaHora };
    setPropuestas(updated);
    saveBorrador(updated[propIdx].id, updated[propIdx].datos_json, { hora: nuevaHora || null });
  }, [propuestas, setPropuestas, saveBorrador]);

  // Punto 2: Abrir modal para mover jugador a otro partido
  const handleOpenMoveModal = useCallback((propIdx, jugador) => {
    setMoveData({ jugador, borradorOrigenId: propuestas[propIdx].id, borradorOrigenIdx: propIdx });
    setShowMoveModal(true);
  }, [propuestas]);

  // Punto 2: Confirmar mover jugador a otro partido
  const handleConfirmMove = useCallback(async (borradorDestinoId, equipoDestino) => {
    if (!moveData) return;
    setSaving(true);
    try {
      await jornadaService.moverJugadorEntreBorradores({
        futbolista_id: moveData.jugador.futbolista_id,
        borrador_origen_id: moveData.borradorOrigenId,
        borrador_destino_id: borradorDestinoId,
        equipo_destino: equipoDestino
      });
      // Recargar propuestas completas
      const res = await jornadaService.getPropuestaPartidos(jornadaId, torneoId);
      if (res.success) setPropuestas(res.data);
      toast.success(`${moveData.jugador.nombre} movido correctamente`);
    } catch (err) {
      toast.error('Error al mover jugador');
      console.error(err);
    } finally {
      setSaving(false);
      setShowMoveModal(false);
      setMoveData(null);
    }
  }, [moveData, jornadaId, torneoId, setPropuestas]);

  // Confirmar UN solo borrador (con verificación de solapados)
  const handleConfirmarUno = useCallback(async (borradorId, forzar = false) => {
    if (!forzar && !window.confirm('¿Confirmar este partido? Se creará el partido definitivo y se eliminará el borrador.')) return;
    setConfirming(true);
    try {
      const res = await jornadaService.confirmarBorrador(borradorId, forzar);

      if (res.requiere_confirmacion) {
        // Hay jugadores solapados — mostrar modal
        setSolapadosData({ borradorId, solapados: res.solapados });
        setShowSolapadosModal(true);
        return;
      }

      if (res.success) {
        toast.success(res.message);
        if (onConfirmed) onConfirmed();
      }
    } catch (err) {
      toast.error('Error al confirmar borrador');
      console.error(err);
    } finally {
      setConfirming(false);
    }
  }, [onConfirmed]);

  // Forzar confirmación tras aceptar solapados
  const handleForzarConfirmacion = useCallback(async () => {
    if (!solapadosData) return;
    setShowSolapadosModal(false);
    await handleConfirmarUno(solapadosData.borradorId, true);
    setSolapadosData(null);
  }, [solapadosData, handleConfirmarUno]);

  // Confirmar todos los borradores
  const handleConfirmarTodos = useCallback(async () => {
    if (!window.confirm('¿Confirmar TODOS los borradores? Se crearán los partidos definitivos y se eliminarán los borradores.')) return;
    setConfirming(true);
    try {
      const res = await jornadaService.confirmarBorradores(jornadaId, torneoId);
      if (res.success) {
        toast.success(res.message);
        if (onConfirmed) onConfirmed();
      }
    } catch (err) {
      toast.error('Error al confirmar borradores');
      console.error(err);
    } finally {
      setConfirming(false);
    }
  }, [jornadaId, torneoId, onConfirmed]);

  // === HANDLERS AÑADIR JUGADOR AL BORRADOR ===

  const closeAddPlayerModal = useCallback(() => {
    setShowAddPlayerModal(false);
    setAddPlayerTarget(null);
    setAddPlayerSearch('');
    setAddPlayerResults([]);
    setAllPlayers([]);
    setJornadaSeleccionada({});
  }, []);

  const [allPlayers, setAllPlayers] = useState([]); // lista completa precargada
  const [jornadaSeleccionada, setJornadaSeleccionada] = useState({}); // { futbolista_id: jornada_id }

  const handleOpenAddPlayer = useCallback(async (borradorId, equipo) => {
    setAddPlayerTarget({ borradorId, equipo });
    setAddPlayerSearch('');
    setAddPlayerResults([]);
    setJornadaSeleccionada({});
    setShowAddPlayerModal(true);
    // Precargar todos los futbolistas disponibles
    setAddPlayerLoading(true);
    try {
      const res = await jornadaService.getFutbolistasParaBorrador(borradorId, '');
      const data = res.success ? res.data : [];
      setAllPlayers(data);
      setAddPlayerResults(data);
    } catch (err) {
      console.error('Error cargando futbolistas:', err);
      toast.error('Error al cargar futbolistas disponibles');
      setShowAddPlayerModal(false);
      setAddPlayerTarget(null);
    } finally {
      setAddPlayerLoading(false);
    }
  }, []);

  const handleSearchPlayers = useCallback((searchText) => {
    setAddPlayerSearch(searchText);
    if (!searchText || searchText.trim().length === 0) {
      setAddPlayerResults(allPlayers);
      return;
    }
    const lower = searchText.toLowerCase();
    setAddPlayerResults(allPlayers.filter(f =>
      f.nombre?.toLowerCase().includes(lower)
    ));
  }, [allPlayers]);

  const handleAddPlayer = useCallback(async (futbolista, accion) => {
    if (!addPlayerTarget) return;
    const jornadaOrigenId = accion === 'traer'
      ? (jornadaSeleccionada[futbolista.futbolista_id] || (futbolista.jornadas_futuras?.[0]?.jornada_id ?? null))
      : null;
    setAddingPlayer(true);
    try {
      const res = await jornadaService.añadirJugadorABorrador(addPlayerTarget.borradorId, {
        futbolista_id: futbolista.futbolista_id,
        equipo: addPlayerTarget.equipo,
        accion,
        jornada_origen_id: jornadaOrigenId
      });
      if (res.success) {
        toast.success(`${futbolista.nombre} añadido al borrador`);
        // Recargar propuestas
        const propRes = await jornadaService.getPropuestaPartidos(jornadaId, torneoId);
        if (propRes.success) setPropuestas(propRes.data);
        closeAddPlayerModal();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al añadir jugador');
      console.error(err);
    } finally {
      setAddingPlayer(false);
    }
  }, [addPlayerTarget, jornadaId, torneoId, setPropuestas, closeAddPlayerModal]);

  // === RENDER ===

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando propuestas de partidos...</p>
      </div>
    );
  }

  if (!propuestas || propuestas.length === 0) {
    return (
      <Alert variant="info" className="mt-4">
        <FaInfoCircle className="me-2" />
        No hay ninguna propuesta de partidos generada para esta jornada en la tabla borrador.
      </Alert>
    );
  }

  // Agrupar propuestas por categoría
  const propuestasPorCategoria = propuestas.reduce((acc, prop) => {
    const catName = prop.categoria_nombre || 'Sin Categoría';
    if (!acc[catName]) acc[catName] = [];
    acc[catName].push(prop);
    return acc;
  }, {});

  // Índice global de cada borrador para los handlers
  const propuestaIdxMap = {};
  propuestas.forEach((p, idx) => { propuestaIdxMap[p.id] = idx; });

  return (
    <div className="proposed-matches-view mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 d-flex align-items-center text-primary fw-bold">
          <FaFutbol className="me-2" />
          PROPUESTA DE ORGANIZACIÓN (BORRADOR)
        </h4>
        <div className="d-flex gap-2 align-items-center">
          {saving && <Spinner animation="border" size="sm" variant="secondary" />}
          <Button
            variant="success"
            onClick={handleConfirmarTodos}
            disabled={confirming || saving}
            className="d-flex align-items-center gap-2"
          >
            {confirming ? <Spinner animation="border" size="sm" /> : <FaCheck />}
            Confirmar Todos los Partidos
          </Button>
        </div>
      </div>

      {Object.entries(propuestasPorCategoria).map(([categoria, matches]) => (
        <div key={categoria} className="mb-5 p-3 bg-white rounded shadow-sm border">
          <div className="d-flex align-items-center mb-4 border-bottom pb-2">
            <h3 className="mb-0 text-dark fw-bold" style={{ letterSpacing: '1px' }}>
              CATEGORÍA: <span className="text-primary">{categoria.toUpperCase()}</span>
            </h3>
            <span className="text-muted ms-3 small">({matches.length} partidos)</span>
          </div>

          <Row>
            {matches.map((match) => {
              const globalIdx = propuestaIdxMap[match.id];
              const jugadores = Array.isArray(match.datos_json) ? match.datos_json : [];
              const equipo1 = jugadores.filter(j => j.equipo === 1);
              const equipo2 = jugadores.filter(j => j.equipo === 2);

              return (
                <Col key={match.id} lg={12} className="mb-4">
                  <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '12px' }}>
                    <Card.Header className="bg-dark text-white py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-bold">
                          PARTIDO <small className="ms-1 text-info">#{match.id}</small>
                        </span>
                        <div className="d-flex gap-3 align-items-center">
                          {/* Punto 4: Modalidad */}
                          <div className="d-flex align-items-center gap-1">
                            <small className="text-light">Modalidad:</small>
                            <Form.Select
                              size="sm"
                              value={match.modalidad || 'F8'}
                              onChange={(e) => handleChangeModalidad(globalIdx, e.target.value)}
                              style={{ width: '80px', fontSize: '12px' }}
                              className="bg-secondary text-white border-0"
                            >
                              {MODALIDADES.map(m => <option key={m} value={m}>{m}</option>)}
                            </Form.Select>
                          </div>
                          {/* Punto 5: Hora */}
                          <div className="d-flex align-items-center gap-1">
                            <FaClock className="text-light" />
                            <Form.Control
                              type="time"
                              size="sm"
                              value={match.hora || ''}
                              onChange={(e) => handleChangeHora(globalIdx, e.target.value)}
                              style={{ width: '110px', fontSize: '12px' }}
                              className="bg-secondary text-white border-0"
                              placeholder="Sin hora"
                            />
                          </div>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleConfirmarUno(match.id)}
                            disabled={confirming || saving}
                            style={{ fontSize: '11px', padding: '2px 8px' }}
                          >
                            <FaCheck className="me-1" />Confirmar
                          </Button>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <Row className="g-0">
                        {[
                          { jugadores: equipo1, num: 1, color: 'primary' },
                          { jugadores: equipo2, num: 2, color: 'danger' }
                        ].map(({ jugadores: eqJugadores, num, color }) => (
                          <Col md={6} key={num} className={num === 1 ? 'border-end' : ''}>
                            <div className="p-3">
                              <h6 className={`d-flex align-items-center text-${color} fw-bold mb-3`}>
                                <div className={`bg-${color} text-white rounded-circle me-2 d-flex align-items-center justify-content-center`} style={{ width: '24px', height: '24px', fontSize: '12px' }}>{num}</div>
                                EQUIPO {num} <span className="text-muted ms-1" style={{ fontSize: '10px', fontWeight: 'normal' }}>({eqJugadores.length} jug.)</span>
                              </h6>
                              <Table hover size="sm" className="mb-0 custom-table">
                                <thead>
                                  <tr className="text-muted small" style={{ fontSize: '11px', backgroundColor: '#f8f9fa' }}>
                                    <th>JUGADOR / AÑO</th>
                                    <th className="text-center" style={{ width: '55px' }}>P.ASIG</th>
                                    <th className="text-center" style={{ width: '45px' }}>P1</th>
                                    <th className="text-center" style={{ width: '45px' }}>P2</th>
                                    <th className="text-center" style={{ width: '55px' }}>TIPO</th>
                                    <th style={{ width: '140px' }}>CLUB / DIV</th>
                                    <th className="text-center" style={{ width: '130px' }}>CAMBIAR POS</th>
                                    <th className="text-end" style={{ width: '70px' }}>MOVER</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {eqJugadores.map((j) => (
                                    <tr key={j.futbolista_id} style={{ verticalAlign: 'middle', backgroundColor: POS_ROW_COLOR[j.posicion_asignada] || 'transparent' }}>
                                      <td>
                                        <span className="fw-bold small">{j.nombre}</span>
                                        <span className="text-muted small ms-1">({j.ano_nacimiento || '?'})</span>
                                      </td>
                                      <td className="text-center">
                                        <span className="fw-bold small">{POS_LABEL[j.posicion_asignada] || j.posicion_asignada}</span>
                                      </td>
                                      <td className="text-center">
                                        <span className="small">{getPosNombre(j.posicion_1)}</span>
                                      </td>
                                      <td className="text-center">
                                        <span className="small text-muted">{j.posicion_2 ? getPosNombre(j.posicion_2) : '-'}</span>
                                      </td>
                                      <td className="text-center">
                                        <span className="small text-muted" style={{ fontSize: '10px' }}>{(j.tipo_asignacion || '').replace(' (P1)', '').replace(' (P2)', '')}</span>
                                      </td>
                                      <td>
                                        {j.club_nombre && (
                                          <span className="small fw-semibold d-block" style={{ fontSize: '10px' }}>
                                            {j.club_nombre}
                                          </span>
                                        )}
                                        <span className={`small ${j.division_nombre?.toLowerCase().includes('sin equipo') ? 'text-danger fw-bold' : 'text-muted'}`} style={{ fontSize: '10px' }}>
                                          {j.division_nombre}
                                        </span>
                                      </td>
                                      {/* Punto 3: Selector + botón para cambiar posición */}
                                      <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                          <Form.Select
                                            size="sm"
                                            defaultValue={j.posicion_asignada || ''}
                                            id={`pos-${match.id}-${j.futbolista_id}`}
                                            style={{ fontSize: '10px', padding: '1px 2px', width: '60px' }}
                                          >
                                            {POSICIONES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                          </Form.Select>
                                          <Button
                                            variant="outline-success"
                                            size="sm"
                                            title="Aplicar cambio de posición"
                                            style={{ padding: '0px 4px', fontSize: '10px' }}
                                            disabled={saving}
                                            onClick={() => {
                                              const sel = document.getElementById(`pos-${match.id}-${j.futbolista_id}`);
                                              if (sel && sel.value !== j.posicion_asignada) {
                                                handleChangePosicion(globalIdx, j.futbolista_id, sel.value);
                                              }
                                            }}
                                          >
                                            <FaCheck />
                                          </Button>
                                        </div>
                                      </td>
                                      <td className="text-end">
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          title={`Mover a Equipo ${num === 1 ? 2 : 1}`}
                                          onClick={() => handleSwapEquipo(globalIdx, j.futbolista_id)}
                                          style={{ padding: '1px 4px', fontSize: '10px' }}
                                          className="me-1"
                                          disabled={saving}
                                        >
                                          <FaExchangeAlt />
                                        </Button>
                                        <Button
                                          variant="outline-info"
                                          size="sm"
                                          title="Mover a otro partido"
                                          onClick={() => handleOpenMoveModal(globalIdx, j)}
                                          style={{ padding: '1px 4px', fontSize: '10px' }}
                                          disabled={saving || propuestas.length < 2}
                                        >
                                          <FaArrowRight />
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                              <div className="text-center mt-2 mb-1">
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleOpenAddPlayer(match.id, num)}
                                  style={{ fontSize: '11px', padding: '2px 10px' }}
                                  disabled={saving}
                                >
                                  <FaUserPlus className="me-1" />Añadir Jugador
                                </Button>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      ))}

      {/* Modal para mover jugador a otro partido (Punto 2) */}
      <MoverJugadorModal
        show={showMoveModal}
        onHide={() => { setShowMoveModal(false); setMoveData(null); }}
        jugador={moveData?.jugador}
        borradorOrigenId={moveData?.borradorOrigenId}
        propuestas={propuestas}
        onConfirm={handleConfirmMove}
        saving={saving}
      />

      {/* Modal de solapados */}
      <Modal show={showSolapadosModal} onHide={() => { setShowSolapadosModal(false); setSolapadosData(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>⚠️ Jugadores en otros borradores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small">Los siguientes jugadores también están en otros borradores. Si confirmas, se eliminarán automáticamente de esos borradores:</p>
          {solapadosData?.solapados?.map((s) => (
            <div key={s.borrador_id} className="mb-2 p-2 border rounded" style={{ fontSize: '13px' }}>
              <strong>Borrador #{s.borrador_id}</strong> — {s.categoria}
              <ul className="mb-0 mt-1">
                {s.jugadores.map(j => (
                  <li key={j.futbolista_id}>{j.nombre}</li>
                ))}
              </ul>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => { setShowSolapadosModal(false); setSolapadosData(null); }}>Cancelar</Button>
          <Button variant="warning" size="sm" onClick={handleForzarConfirmacion} disabled={confirming}>
            {confirming ? <Spinner animation="border" size="sm" /> : 'Confirmar y quitar de otros borradores'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Añadir Jugador al Borrador */}
      <Modal show={showAddPlayerModal} onHide={closeAddPlayerModal} centered size="lg">
        <Modal.Header closeButton className="bg-success text-white py-2" closeVariant="white">
          <Modal.Title style={{ fontSize: '16px' }}>
            <FaUserPlus className="me-2" />Añadir Jugador al Equipo {addPlayerTarget?.equipo}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3 align-items-center">
            <Form.Control
              type="text"
              placeholder="Filtrar por nombre..."
              value={addPlayerSearch}
              onChange={(e) => handleSearchPlayers(e.target.value)}
              autoFocus
              size="sm"
            />
            {addPlayerLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <span className="text-muted small text-nowrap">{addPlayerResults.length} de {allPlayers.length}</span>
            )}
          </div>

          {addPlayerResults.length > 0 && (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                <thead>
                  <tr className="text-muted" style={{ fontSize: '11px', backgroundColor: '#f8f9fa' }}>
                    <th>JUGADOR</th>
                    <th className="text-center">SALDO</th>
                    <th className="text-center">JORNADAS FUTURAS</th>
                    <th className="text-center">ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {addPlayerResults.map((f) => (
                    <tr key={f.futbolista_id} style={{ verticalAlign: 'middle' }}>
                      <td>
                        <span className="fw-bold">{f.nombre}</span>
                        <span className="text-muted ms-1">({f.ano_nacimiento || '?'})</span>
                        {f.categoria_nombre && <span className="text-muted ms-1 small">— {f.categoria_nombre}</span>}
                        {f.sin_inscripcion && <span className="badge bg-danger ms-1" style={{ fontSize: '9px' }}>Sin inscripción</span>}
                      </td>
                      <td className="text-center">
                        <span className={`badge ${f.saldo_jornadas > 0 ? 'bg-success' : 'bg-secondary'}`}>
                          {f.saldo_jornadas}
                        </span>
                      </td>
                      <td className="text-center">
                        {f.jornadas_futuras && f.jornadas_futuras.length > 0 ? (
                          <span className="badge bg-info">{f.jornadas_futuras.length} jornada(s)</span>
                        ) : (
                          <span className="text-muted small">Ninguna</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex flex-column gap-1 align-items-center">
                          {!f.sin_inscripcion && f.jornadas_futuras && f.jornadas_futuras.length > 0 && (
                            <div className="d-flex gap-1 align-items-center">
                              {f.jornadas_futuras.length > 1 && (
                                <Form.Select
                                  size="sm"
                                  style={{ fontSize: '10px', padding: '1px 2px', width: '110px' }}
                                  value={jornadaSeleccionada[f.futbolista_id] || f.jornadas_futuras[0].jornada_id}
                                  onChange={(e) => setJornadaSeleccionada(prev => ({ ...prev, [f.futbolista_id]: parseInt(e.target.value) }))}
                                >
                                  {f.jornadas_futuras.map(j => (
                                    <option key={j.jornada_id} value={j.jornada_id}>
                                      J{j.jornada_numero} ({formatFechaJornadaCorta(j.jornada_fecha)})
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                              <Button
                                variant="warning"
                                size="sm"
                                style={{ fontSize: '10px', padding: '2px 6px' }}
                                disabled={addingPlayer}
                                onClick={() => handleAddPlayer(f, 'traer')}
                                title="Traer de jornada futura (mover inscripción)"
                              >
                                <FaCalendarAlt className="me-1" />Traer
                              </Button>
                            </div>
                          )}
                          <Button
                            variant="outline-success"
                            size="sm"
                            style={{ fontSize: '10px', padding: '2px 6px' }}
                            disabled={addingPlayer}
                            onClick={() => handleAddPlayer(f, 'invitar')}
                            title={f.sin_inscripcion ? "Invitar (se creará inscripción automática, equipación no pagada)" : "Invitar a esta jornada (sin coste)"}
                          >
                            <FaGift className="me-1" />Invitar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {!addPlayerLoading && addPlayerResults.length === 0 && allPlayers.length === 0 && (
            <Alert variant="info" className="small py-2 mb-0">
              <FaInfoCircle className="me-1" />No hay futbolistas disponibles para este torneo.
            </Alert>
          )}

          {!addPlayerLoading && addPlayerResults.length === 0 && allPlayers.length > 0 && addPlayerSearch.length > 0 && (
            <Alert variant="warning" className="small py-2 mb-0">
              <FaInfoCircle className="me-1" />No se encontraron futbolistas para &quot;{addPlayerSearch}&quot;.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={closeAddPlayerModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-table th { border-top: none; background-color: #f8f9fa; }
        .custom-table td { padding: 6px 4px; }
        .proposed-matches-view h3 { font-family: 'Inter', sans-serif; }
      `}} />
    </div>
  );
};

// --- Modal para mover jugador a otro partido ---
const MoverJugadorModal = ({ show, onHide, jugador, borradorOrigenId, propuestas, onConfirm, saving }) => {
  const [selectedBorrador, setSelectedBorrador] = useState('');
  const [selectedEquipo, setSelectedEquipo] = useState(1);

  const destinos = (propuestas || []).filter(p => p.id !== borradorOrigenId);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '16px' }}>
          Mover <strong>{jugador?.nombre}</strong> a otro partido
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold small">Partido destino</Form.Label>
          <Form.Select value={selectedBorrador} onChange={(e) => setSelectedBorrador(e.target.value)}>
            <option value="">Selecciona un partido...</option>
            {destinos.map(p => (
              <option key={p.id} value={p.id}>
                #{p.id} — {p.categoria_nombre || 'Sin Categoría'} ({(Array.isArray(p.datos_json) ? p.datos_json : []).length} jug.)
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label className="fw-bold small">Equipo destino</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check type="radio" label="Equipo 1" checked={selectedEquipo === 1} onChange={() => setSelectedEquipo(1)} />
            <Form.Check type="radio" label="Equipo 2" checked={selectedEquipo === 2} onChange={() => setSelectedEquipo(2)} />
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>Cancelar</Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!selectedBorrador || saving}
          onClick={() => onConfirm(parseInt(selectedBorrador), selectedEquipo)}
        >
          {saving ? <Spinner animation="border" size="sm" /> : 'Mover Jugador'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProposedMatchesView;
